import React from "react";
import { t } from "i18next";
import { Dropdown, Input, List, Typography, Flex, Spin, Space, Button } from "antd";
import { RightOutlined, SearchOutlined, CloseCircleFilled, ContactsOutlined, ShopOutlined } from "@ant-design/icons";
import { SellerIcon } from "../icon";

const { Text } = Typography;

type TwoPaneOption = {
    value: any;
    label: React.ReactNode;
    disabled?: boolean;
    children?: TwoPaneOption[];
    _labelText?: string;
    _searchText?: string;
    _item?: any;
    _meta?: any;
};

export function TwoPaneCascaderSearch(props: {
    placeholder: string;
    options: TwoPaneOption[];

    open: boolean;
    onOpenChange: (open: boolean) => void;

    loading: boolean;
    searchTerm: string;
    inputValue: string;
    setInputValue: (v: string) => void;

    onSearch: (q: string) => void;

    onChange: (valuePath: any[], selected: TwoPaneOption[]) => void;

    allowClear?: boolean;

    notFoundContent?: React.ReactNode;

    dropdownWidth?: number;

    footer?: React.ReactNode;
}) {
    const {
        placeholder,
        options,
        open,
        onOpenChange,
        loading,
        searchTerm,
        inputValue,
        setInputValue,
        onSearch,
        onChange,
        allowClear = true,
        notFoundContent,
        dropdownWidth = 400,
        footer,
    } = props;

    const [activeParentValue, setActiveParentValue] = React.useState<any>(null);
    const [selectedPath, setSelectedPath] = React.useState<any[] | null>(null);
    const [isSearching, setIsSearching] = React.useState(false);
    const [hoveredParentValue, setHoveredParentValue] = React.useState<any>(null);



    const norm = (s: unknown) =>
        String(s ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .trim();

    const filteredParents = React.useMemo(() => {
        if (isSearching) return [];

        const q = norm(inputValue);
        if (!q) return options;

        const searchTerms = q.split(/\s+/).filter(Boolean);

        return options.filter((p) => {
            const searchText = norm((p as any)?._searchText);
            return searchTerms.every((term) => searchText.includes(term));
        });
    }, [options, inputValue, isSearching]);
    React.useEffect(() => {
        if (selectedPath?.[0] != null) {
            setActiveParentValue(selectedPath[0]);
            return;
        }
        setActiveParentValue(filteredParents[0]?.value ?? null);
    }, [filteredParents, selectedPath]);

    React.useEffect(() => {
        if (!loading) {
            setIsSearching(false);
        }
    }, [loading, options]);

    const activeParent = React.useMemo(() => {
        const list = filteredParents.length ? filteredParents : options;
        return list.find((p) => p.value === activeParentValue) ?? list[0] ?? null;
    }, [filteredParents, options, activeParentValue]);


    const displayValue = React.useMemo(() => {
        if (open) return inputValue;

        if (!selectedPath) return "";
        const [pVal] = selectedPath;
        const p = options.find((x) => x.value === pVal);
        return (p as any)?._labelText ?? "";
    }, [open, inputValue, selectedPath, options]);

    const clear = () => {
        setSelectedPath(null);
        setInputValue("");
        onChange([], []);
    };

    const resolveRoles = (parent: TwoPaneOption): TwoPaneOption[] => {
        const children = Array.isArray(parent.children) ? parent.children : [];
        const order = ["customer", "store", "seller"];
        return [...children].sort((a, b) => {
            const ai = order.indexOf(String(a.value));
            const bi = order.indexOf(String(b.value));
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
    };

    const selectInlineRole = (parent: TwoPaneOption, roleNode: TwoPaneOption) => {
        if (parent.disabled || roleNode.disabled) return;

        const valuePath = [parent.value, roleNode.value];
        const selectedNodes = [parent, roleNode];

        setSelectedPath(valuePath);
        onChange(valuePath, selectedNodes);

        onOpenChange(false);
        setInputValue("");
    };

    const resolveRoleUI = (roleValue: unknown) => {
        const val = String(roleValue).toLowerCase();

        if (val.startsWith("customer")) {
            return {
                label: t("contacts.role.customer"),
                icon: <ContactsOutlined />,
            };
        }
        if (val.startsWith("store")) {
            return {
                label: t("contacts.role.store_operator"),
                icon: <ShopOutlined />,
            };
        }
        if (val.startsWith("seller")) {
            return {
                label: t("contacts.role.seller_operator"),
                icon: <SellerIcon />,
            };
        }

        return {
            label: "",
            icon: null,
        };
    };


    const overlay = (
        <div
            style={{
                width: dropdownWidth,
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 10px 26px rgba(0,0,0,0.14)",
                display: "flex",
                flexDirection: "column",
                minHeight: 280,
                maxHeight: 410,
                transform: "translate(-12px, 4px)"
            }}
            onMouseDown={(e) => {
                e.preventDefault();
            }}
        >
            {/* LEFT */}
            <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>

                {isSearching ? (
                    <Flex justify="center" align="center" style={{ padding: 16 }}>
                        <Spin size="small" />
                    </Flex>
                ) : loading ? (
                    <Flex justify="center" align="center" style={{ padding: 16 }}>
                        <Spin size="small" />
                    </Flex>
                ) : filteredParents.length === 0 ? (
                    <div style={{ padding: 16 }}>
                        {notFoundContent ?? (
                            <div style={{ textAlign: "center", color: "rgba(0,0,0,0.45)" }}>
                                {searchTerm ? `No contacts found for "${searchTerm}"` : `No contacts available`}
                            </div>
                        )}
                    </div>
                ) : (
                    <List
                        size="small"
                        dataSource={filteredParents}
                        renderItem={(p) => {
                            const hovered = p.value === hoveredParentValue;

                            const bg = hovered ? "rgba(0, 0, 0, 0.03)" : "transparent";
                            const roles = resolveRoles(p);

                            return (
                                <List.Item
                                    style={{
                                        cursor: p.disabled ? "not-allowed" : "default",
                                        background: bg,
                                        opacity: p.disabled ? 0.55 : 1,
                                        paddingInline: 18,
                                        paddingBlock: 10,
                                        alignItems: "stretch",
                                    }}
                                    onMouseEnter={() => setHoveredParentValue(p.value)}
                                    onMouseLeave={() => setHoveredParentValue(null)}
                                >
                                    <div style={{ width: "100%" }}>
                                        {/* Header: tu label actual */}
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <div style={{ width: "100%" }}>{p.label}</div>
                                        </div>

                                        {/* Acciones inline */}
                                        <div style={{ marginTop: 8 }}>
                                            {roles.length === 0 ? (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    Sin roles
                                                </Text>
                                            ) : (
                                                <Space size={8} wrap>
                                                    <Flex gap={8} style={{ flexDirection: "row", flexWrap: "wrap" }}>
                                                        {roles.map((role) => {
                                                            const ui = resolveRoleUI(role.value);
                                                            return (
                                                                <Button
                                                                    key={String(role.value)}
                                                                    size="small"
                                                                    disabled={p.disabled || role.disabled}
                                                                    onClick={() => selectInlineRole(p, role)}
                                                                >
                                                                    {role.label ?? (
                                                                        <Space size={4}>
                                                                            {ui.icon}
                                                                            <span>{ui.label}</span>
                                                                        </Space>
                                                                    )}
                                                                </Button>
                                                            );
                                                        })}
                                                    </Flex>
                                                </Space>
                                            )}
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                )}
            </div>

            {footer ? (
                <div
                    style={{
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                        padding: "8px 12px",
                        flexShrink: 0,
                        background: "#fff",
                        borderBottomLeftRadius: 10,
                        borderBottomRightRadius: 10,
                    }}
                >
                    {footer}
                </div>
            ) : null}
        </div>
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={onOpenChange}
            dropdownRender={() => overlay}
            trigger={["click"]}
        >
            <Input
                readOnly={false}
                value={displayValue}
                placeholder={placeholder}
                autoComplete="new-password"
                onClick={() => onOpenChange(true)}
                onChange={(e) => {
                    const v = e.target.value;
                    setInputValue(v);
                    setIsSearching(true);
                    onSearch(v);
                    if (!open) onOpenChange(true);
                }}
                suffix={
                    allowClear && (selectedPath || inputValue) ? (
                        <CloseCircleFilled
                            onClick={(e) => {
                                e.stopPropagation();
                                clear();
                            }}
                            style={{ color: "rgba(0,0,0,0.25)" }}
                        />
                    ) : (
                        <SearchOutlined style={{ color: "rgba(0,0,0,0.25)" }} />
                    )
                }
                style={{ width: "100%" }}
            />
        </Dropdown>
    );
}
